import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import { StaticQuery, graphql } from 'gatsby';

function absoluteUrl(baseUrl, path) {
  if (!path) {
    return null;
  }
  if (path.startsWith('http')) {
    return path;
  }
  const base = typeof baseUrl === 'string' ? baseUrl : '';
  const cleanBase = base.endsWith('/') ? base.slice(0, -1) : base;
  const cleanPath = path.startsWith('/') ? path : '/' + path;
  return cleanBase + cleanPath;
}

function SEO({
  description,
  lang,
  meta,
  keywords,
  title,
  image,
  imageWidth,
  imageHeight,
  pathname,
  type
}) {
  return (
    <StaticQuery
      query={detailsQuery}
      render={data => {
        const { siteMetadata } = data.site;
        const metaDescription = description || siteMetadata.description;
        const imagePath = image || siteMetadata.defaultOgImage;
        const imageUrl = absoluteUrl(siteMetadata.baseUrl, imagePath);
        const pageUrl = pathname
          ? absoluteUrl(siteMetadata.siteUrl, pathname).replace(/\/$/, '')
          : null;
        const ogImageWidth = imageWidth || siteMetadata.defaultOgImageWidth;
        const ogImageHeight = imageHeight || siteMetadata.defaultOgImageHeight;
        const twitterCard = imageUrl ? `summary_large_image` : `summary`;

        return (
          <Helmet
            htmlAttributes={{
              lang
            }}
            title={title}
            titleTemplate={`%s | ${siteMetadata.title}`}
            meta={[
              {
                name: `description`,
                content: metaDescription
              },
              {
                property: `og:title`,
                content: title
              },
              {
                property: `og:description`,
                content: metaDescription
              },
              {
                property: `og:type`,
                content: type
              },
              {
                property: `og:site_name`,
                content: siteMetadata.title
              },
              pageUrl && {
                property: `og:url`,
                content: pageUrl
              },
              {
                name: `twitter:card`,
                content: twitterCard
              },
              siteMetadata.twitter &&
                siteMetadata.twitter.name && {
                  name: `twitter:site`,
                  content: `@${siteMetadata.twitter.name}`
                },
              {
                name: `twitter:creator`,
                content: siteMetadata.author
              },
              {
                name: `twitter:title`,
                content: title
              },
              {
                name: `twitter:description`,
                content: metaDescription
              },
              {
                name: 'theme-color',
                content: '#333'
              },
              imageUrl && {
                property: `og:image`,
                content: imageUrl
              },
              imageUrl && {
                name: `twitter:image`,
                content: imageUrl
              },
              imageUrl &&
                ogImageWidth && {
                  property: `og:image:width`,
                  content: String(ogImageWidth)
                },
              imageUrl &&
                ogImageHeight && {
                  property: `og:image:height`,
                  content: String(ogImageHeight)
                }
            ]
              .filter(Boolean)
              .concat(
                keywords.length > 0
                  ? {
                      name: `keywords`,
                      content: keywords.join(`, `)
                    }
                  : []
              )
              .concat(meta)}
          />
        );
      }}
    />
  );
}

SEO.defaultProps = {
  lang: `en`,
  meta: [],
  keywords: [],
  type: `website`
};

SEO.propTypes = {
  description: PropTypes.string,
  image: PropTypes.string,
  imageHeight: PropTypes.number,
  imageWidth: PropTypes.number,
  lang: PropTypes.string,
  meta: PropTypes.array,
  keywords: PropTypes.arrayOf(PropTypes.string),
  pathname: PropTypes.string,
  title: PropTypes.string.isRequired,
  type: PropTypes.string
};

export default SEO;

const detailsQuery = graphql`
  query DefaultSEOQuery {
    site {
      siteMetadata {
        title
        description
        author
        baseUrl
        siteUrl
        defaultOgImage
        defaultOgImageWidth
        defaultOgImageHeight
        twitter {
          name
        }
      }
    }
  }
`;
